/** Isolated reader protocol. Legacy page-audio/provider routes remain untouched. */
const API_BASE = import.meta.env.VITE_API_URL || '';

export class ReaderAudioError extends Error {
  constructor(public status: number, public data: { code: string; retryable?: boolean }) {
    super(data.code);
    this.name = 'ReaderAudioError';
  }
}

export interface ReaderSpeechMark {
  type: 'word' | 'sentence';
  value: string;
  time: number;
  endTime?: number;
  alignmentGapBefore?: boolean;
  sourceGapBefore?: string;
  audioSha256?: string;
}
export interface ReaderAudioPage {
  status: 'missing' | 'pending' | 'processing' | 'ready' | 'error';
  isReady: boolean;
  errorCode?: string | null;
  retryable?: boolean;
  textSource?: 'pdf_text' | 'ocr_text';
  alignmentMode?: 'pdf_text' | 'ocr_transcript';
  textPreparation?: { phase: string; completedPages: number; targetPage: number; currentPage: number | null };
}
export interface ReaderAudioStatus {
  success: boolean;
  page: ReaderAudioPage | null;
  isReady?: boolean;
  job?: { status: string } | null;
}
export interface ReaderRecording {
  success: boolean;
  audioUrl: string;
  audioSha256: string;
  durationMs: number;
  language: string;
  textSource: 'pdf_text' | 'ocr_text';
  alignmentMode: 'pdf_text' | 'ocr_transcript';
}
export interface ReaderMarks {
  success: boolean;
  audioSha256: string;
  marks: ReaderSpeechMark[];
}
export interface ReaderWordPosition {
  success: boolean;
  audioSha256: string;
  position: { timeMs: number; endTimeMs: number; wordIndex: number; endWordIndexExclusive: number };
}

async function request<T>(path: string, token: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}/api/reader-audio/document/${path}`, {
    ...options,
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok || !body || body.success === false) {
    throw new ReaderAudioError(response.status || 502, {
      code: typeof body?.code === 'string' ? body.code : 'PAGE_AUDIO_REQUEST_FAILED',
      retryable: body?.retryable === true,
    });
  }
  return body as T;
}
const pagePath = (id: string, page: number) => `${encodeURIComponent(id)}/page/${page}`;
export const readerAudioApi = {
  getStatus(id: string, token: string, page: number, signal?: AbortSignal) {
    return request<ReaderAudioStatus>(`${encodeURIComponent(id)}/status?pageNumber=${page}`, token, { signal });
  },
  generatePageAudio(id: string, page: number, token: string, signal?: AbortSignal) {
    return request<{ success: boolean; status: string; cached: boolean; textPreparation?: ReaderAudioPage['textPreparation'] }>(`${pagePath(id, page)}/generate`, token, { method: 'POST', signal });
  },
  getPageAudioUrl(id: string, page: number, token: string, signal?: AbortSignal) {
    return request<ReaderRecording>(`${pagePath(id, page)}/url`, token, { signal });
  },
  getSpeechMarks(id: string, page: number, token: string, signal?: AbortSignal) {
    return request<ReaderMarks>(`${pagePath(id, page)}/marks`, token, { signal });
  },
  findWordPosition(id: string, page: number, text: string, token: string, signal?: AbortSignal) {
    return request<ReaderWordPosition>(`${pagePath(id, page)}/find-position`, token, {
      method: 'POST', body: JSON.stringify({ text, startFrom: 0 }), signal,
    });
  },
};

/** A selection, marks and media must refer to the same immutable recording. */
export function assertReaderRecording(audio: ReaderRecording, marks: ReaderMarks, selectionHash?: string) {
  const hash = audio.audioSha256;
  if (typeof hash !== 'string' || !/^[a-f0-9]{64}$/.test(hash)
    || marks.audioSha256 !== hash || (selectionHash !== undefined && selectionHash !== hash)
    || (Array.isArray(marks.marks) && marks.marks.some(mark => mark.audioSha256 !== undefined && mark.audioSha256 !== hash))) {
    throw new ReaderAudioError(409, { code: 'PAGE_AUDIO_IDENTITY_MISMATCH', retryable: true });
  }
  const nativeText = audio.alignmentMode === 'pdf_text' && audio.textSource === 'pdf_text';
  const scannedText = audio.alignmentMode === 'ocr_transcript' && audio.textSource === 'ocr_text';
  if ((!nativeText && !scannedText) || !/^(en|ar)(-|$)/i.test(audio.language || '') || !Array.isArray(marks.marks)) {
    throw new ReaderAudioError(422, { code: 'PAGE_AUDIO_ALIGNMENT_FAILED' });
  }
  let previous = -1;
  for (const word of marks.marks.filter(mark => mark.type === 'word')) {
    if (typeof word.value !== 'string' || !word.value.trim() || !Number.isFinite(word.time)
      || word.time < 0 || word.time < previous
      || (/^en(-|$)/i.test(audio.language) && word.endTime === undefined)
      || (word.endTime !== undefined && (!Number.isFinite(word.endTime) || word.endTime <= word.time))) {
      throw new ReaderAudioError(422, { code: 'PAGE_AUDIO_ALIGNMENT_FAILED' });
    }
    previous = word.time;
  }
}
